import React from 'react';

// import components
import CarouselCards from 'components/partials/carousel-cards';

// import types
import { ImageContentCardData, sortImageContentCards } from "types/image-content-card-data";
import { IntroHeadlineProps } from "types/intro-headline-props";
import { PrimaryButtonProps } from "components/partials/buttons/primary-button";
import { AnimalExhibitFilter, ActivityFilter, DealFilter, PerformerFilter, RideFilter, VendorFilter } from "types/filters";

// import helpers
import { getLinkDataFromDato } from "helpers/get-link-data";
import { getImageContentCardsForAnimalExhibits } from "helpers/get-image-content-cards-for-animal-exhibits";
import { getImageContentCardsForActivities } from "helpers/get-image-content-cards-for-activities";
import { getImageContentCardsForDeals } from "helpers/get-image-content-cards-for-deals";
import { getImageContentCardsForPerformers } from "helpers/get-image-content-cards-for-performers";
import { getImageContentCardsForRides } from "helpers/get-image-content-cards-for-rides";
import { getImageContentCardsForVendors } from "helpers/get-image-content-cards-vendors";

const ImageCardCarouselBuilder: React.FC<{ imageCardCarouselData?: Queries.ImageCardCarouselBlockFragment }> = ({ imageCardCarouselData }) => {

    if (!imageCardCarouselData) return null;

    const maxCards: number | undefined = 30;

    let modifierClass = imageCardCarouselData.colorOption ? `pattern-has-bgcolor bg-brand-${imageCardCarouselData.colorOption}` : 'pattern-has-bgcolor bg-brand-red';

    let button: PrimaryButtonProps | undefined = undefined;

    if (imageCardCarouselData.link?.link) {

        const linkData = getLinkDataFromDato(imageCardCarouselData.link?.link);

        if (linkData) {
            button = {
                buttonUrl: linkData.url,
                buttonText: linkData.label,
                target: linkData.target,
            };
        }

    };

    let contentType = imageCardCarouselData.dynamicQuery?.contentType ?? '';

    let cardCarouselIntro: IntroHeadlineProps = {
        headline: imageCardCarouselData.heading ?? '',
        modifierClass: 'btn-primary--darkteal',
        buttonProps: button
    };

    let cards: ImageContentCardData[] = [];

    const signatureEventId = imageCardCarouselData.dynamicQuery?.signatureEvent?.id;

    // content-types: animal-exhibit, activity, deal, performer, ride, vendor
    if (contentType === 'animal-exhibit') {

        const filters: AnimalExhibitFilter[] = imageCardCarouselData.dynamicQuery?.animalExhibitFilters as AnimalExhibitFilter[];

        cards = getImageContentCardsForAnimalExhibits(imageCardCarouselData.cardLinkLabel ?? 'Learn more', filters, signatureEventId);

    } else if (contentType === 'activity') {

        const filters: ActivityFilter[] = imageCardCarouselData.dynamicQuery?.activityFilters as ActivityFilter[];

        cards = getImageContentCardsForActivities(imageCardCarouselData.cardLinkLabel ?? 'Learn more', filters, signatureEventId);

    } else if (contentType === 'deal') {

        const filters: DealFilter[] = imageCardCarouselData.dynamicQuery?.dealFilters as DealFilter[];

        cards = getImageContentCardsForDeals(imageCardCarouselData.cardLinkLabel ?? 'Learn more', filters, signatureEventId);

    } else if (contentType === "performer") {

        const filters: PerformerFilter[] = imageCardCarouselData.dynamicQuery?.performerFilters as PerformerFilter[];

        cards = getImageContentCardsForPerformers(imageCardCarouselData.cardLinkLabel ?? 'Learn more', filters, signatureEventId);

    } else if (contentType === 'ride') {

        const filters: RideFilter[] = imageCardCarouselData.dynamicQuery?.rideFilters as RideFilter[];

        cards = getImageContentCardsForRides(imageCardCarouselData.cardLinkLabel ?? 'Learn more', filters, signatureEventId);

    } else if (contentType === 'vendor') {

        const filters: VendorFilter[] = imageCardCarouselData.dynamicQuery?.vendorFilters as VendorFilter[];

        cards = getImageContentCardsForVendors(imageCardCarouselData.cardLinkLabel ?? 'Learn more', filters, signatureEventId);
    }

    if (cards.length === 0) return null;

    // sort cards by date and then by title
    cards = sortImageContentCards(cards);

    if (maxCards) {
        cards = cards.slice(0, maxCards);
    }
    
    return (
        <CarouselCards intro={cardCarouselIntro} patternModifierClass={modifierClass} cards={cards} />
    );
};

export default ImageCardCarouselBuilder;